import React, { useEffect } from 'react';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';

// SceneStaging — the beat that runs between sitting down and being served.
//
// Sitting in a salon is a request. Somebody should come. This is the smallest
// honest version of that: the room says who and where (ROOMS.<id>.stage) and on
// what cue (ROOMS.<id>.onSit), and this turns the cue into a stage direction.
//
// ── WHY THE DELAY ────────────────────────────────────────────────────────────
// She could start walking on the same frame you land in the chair, and it would
// read as a trigger firing. A few seconds of nothing is what makes it read as
// her noticing — the pause is the performance, not latency to be optimised away.
//
// ── WHY THE BUTTON IS DOM ────────────────────────────────────────────────────
// Every other control in the room is in-scene, which is right for things that
// belong to an object: the chair's own "Stand up" occludes, scales with distance
// and sits in the room's light. This one is different in kind. It exists so you
// can never be stuck in a set piece, and an in-scene button can be looked away
// from — the exact moment you would want it is the moment it is behind you.
//
// It is not a second Stand Up. Standing up is something the player does to the
// chair; this ends the whole beat and sends everyone home, which is why it also
// clears the staging rather than leaving her parked at the basin.
//
// The conversation, if one is open, is separate and closes with its own × on the
// SceneDock. Ending a set piece is not a reason to hang up on somebody.
// ── IT CANNOT LIVE IN THE BOTTOM-RIGHT WHILE A CONVERSATION IS OPEN ──────────
// Khari, 2026-08-06: "There is a 'done' button that allows me to exit at any
// time.. We'd just need to make sure it is visible."
//
// It was not. SceneDock is `bottom`-anchored, centred, and up to
// `100vw - 32px` wide — so on any phone it reaches the right edge, and this
// button (z 73, one above the dock's 72) landed ON its input row. Visible in the
// sense that it painted; unusable, and it stole taps meant for Send.
//
// So while a scene is up it moves to the TOP-right, which the dock never
// occupies. Below CallRing's corner by a full row, because an incoming call and
// a seated scene can coexist and the call has to win that spot (z 90 to this
// button's 73 — it would paint over the top of this one anyway).
const btnBase = {
  position: 'fixed', right: 'max(16px, env(safe-area-inset-right))', zIndex: 73,
  padding: '11px 20px', borderRadius: 999,
  border: '1px solid rgba(255,178,94,0.5)', background: 'rgba(20,12,8,0.9)',
  color: '#ffe9c4', font: '700 14px system-ui', cursor: 'pointer',
  pointerEvents: 'auto', backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};
const btnLow = { ...btnBase, bottom: 'max(16px, calc(env(safe-area-inset-bottom) + 16px))' };
const btnHigh = { ...btnBase, top: 'calc(max(16px, env(safe-area-inset-top)) + 64px)' };

export default function SceneStaging() {
  const interior = useWorldStore((s) => s.interior);
  const seated = useWorldStore((s) => s.seated);
  const scene = useWorldStore((s) => s.scene);
  const stageTo = useWorldStore((s) => s.stageTo);
  const clearStaging = useWorldStore((s) => s.clearStaging);
  const standUp = useWorldStore((s) => s.standUp);

  const cue = ROOMS[interior]?.onSit || null;
  const phase = seated?.phase || null;

  // THE CUE. Armed only once you have actually landed — firing it on 'sitting'
  // would start her walking while the camera is still moving, and the two
  // motions read as one confused shove.
  //
  // The cleanup clearing the timer is what makes standing up early safe: get out
  // of the chair inside the delay and she never sets off at all.
  useEffect(() => {
    if (!cue || phase !== 'seated') return undefined;
    const t = setTimeout(() => stageTo(cue.slug, cue.mark), cue.delayMs ?? 3000);
    return () => clearTimeout(t);
  }, [cue, phase, stageTo]);

  // OUT OF THE CHAIR, BY ANY ROUTE, SENDS HER HOME — the button below, the
  // chair's own Stand up, or world.stand() from the console. Hanging this off
  // the state rather than off the button means there is one way home and no way
  // to leave her stranded at the basin.
  useEffect(() => { if (!seated) clearStaging(); }, [seated, clearStaging]);

  // Leaving the room ends any direction with it. A stage mark names a place in
  // ONE room's floor plan, so carrying it out of the door is meaningless.
  useEffect(() => { clearStaging(); }, [interior, clearStaging]);

  // Shown from the moment you are committed to the chair rather than settled in
  // it: a sit that somehow never completes is precisely the case this exists for.
  //
  // NOT gated on the room having a cue. The point is that being seated always
  // has a visible way out, and "this room has no scripted beat" is the last
  // reason to withhold one — a seat nobody wrote a scene for is exactly where
  // you would be stuck with only the in-scene button, which may be behind you.
  if (!seated || phase === 'standing') return null;

  return (
    <button
      style={scene ? btnHigh : btnLow}
      onClick={() => { standUp(); clearStaging(); }}
      aria-label="End this scene and stand up"
    >
      Done
    </button>
  );
}
